/**
 * HOW MUCH OF THE TIMING STUDIES A TRIAL ACCOUNT SEES.
 *
 * <p>A trial reader gets the studies' HISTORY and not their present: CTS to one month before the
 * last traded bar, PTB to five, PTA to nine. A paying or administrator account sees everything.
 *
 * <pre>
 *                                                      last traded bar
 *   ──────────────────────────────────────────────────────────────┤
 *   CTS   drawn ─────────────────────────────────────┤  withheld ─┤   1 month
 *   PTB   drawn ─────────────────────────┤  withheld ─────────────┤   5 months
 *   PTA   drawn ─────────────┤  withheld ─────────────────────────┤   9 months
 * </pre>
 *
 * <h2>⚠ IT IS PRESENTATIONAL, NOT AN ENTITLEMENT BOUNDARY</h2>
 *
 * The CTS datasets are fetched by the browser straight from the PUBLIC `astrocharts-precomputed`
 * bucket — they do not pass through the backend at all — so the whole 1801-2100 timeline is
 * readable by anyone who opens a network panel. The PTA/PTB study endpoint is authenticated but
 * applies no cutoff of its own. So this withholds the DISPLAY and nothing more, exactly as the
 * browser API key's referrer restrictions stop casual abuse rather than a determined caller.
 *
 * <p>Making it an entitlement means the server declining to answer past the cutoff, which is a
 * different change on a different side of the wire. Stated rather than implied, because a
 * restriction that looks enforced and is not is worse than one nobody mistook for enforcement.
 *
 * <h2>⚠ THE MONTH ARITHMETIC IS CLAMPED, because `setMonth` OVERFLOWS FORWARD</h2>
 *
 * The obvious helper is `d.setMonth(d.getMonth() - n)`. `setMonth` keeps the day-of-month and
 * rolls any overflow into the FOLLOWING month, so when the target month is shorter than the
 * input's day the cutoff lands later than the one that was asked for:
 *
 * <pre>
 *   input        months   setMonth      clamped (this module)   too generous by
 *   2026-10-31     1      2026-10-01    2026-09-30              1 day
 *   2026-03-31     1      2026-03-03    2026-02-28              3 days
 *   2026-07-31     5      2026-03-03    2026-02-28              3 days
 *   2024-02-29    12      2023-03-01    2023-02-28              1 day
 *   2026-05-31     9      2025-08-31    2025-08-31              — August has 31
 * </pre>
 *
 * ⚠ The size of that is ONE TO THREE DAYS, not the month this was first written to claim. The
 * first version of this header said 2026-03-31 minus a month landed "three days AFTER its own
 * input" — it does not, and it cannot: 3 March is 28 days BEFORE 31 March, and an overflow can
 * never carry past the date it started from. Its own control test caught the claim, which is
 * the only reason it is stated correctly here.
 *
 * <p>So this is a small error rather than a dramatic one — and it is still worth not making. It
 * bites on the 31st of five months a year and on the 29th/30th/31st for February, it shows up as
 * a chart that is slightly too generous, and that is indistinguishable from a correct one. The
 * day is clamped to the target month's last day instead, which is the conventional reading of
 * "a month before the 31st" and can only ever be earlier.
 *
 * <h2>⚠ AND IT IS UTC, never the local accessors</h2>
 *
 * `new Date('2026-10-31')` is UTC midnight while `getMonth`/`setMonth` are LOCAL, so west of UTC
 * the arithmetic starts from 30 October and two readers of the same chart get cutoffs a day apart.
 * Every date comparison in this codebase follows the same rule, and this file compares ISO DAY
 * STRINGS rather than Date objects wherever it can: on `YYYY-MM-DD` a lexicographic compare IS a
 * chronological one, it needs no parsing, and no timezone can shift it.
 */

/**
 * HOW FAR BACK EACH STUDY IS CUT, in months.
 *
 * <p>One frozen table rather than three constants, because the three are read together — the
 * status line names all of them when something is withheld — and a fourth study added here is a
 * row rather than an edit at each call site.
 */
export const TRIAL_CUTOFF_MONTHS = Object.freeze({
  CTS: 1,
  PTA: 9,
  PTB: 5,
});

/** The ISO day of a chart date or an intraday timestamp. The whole of the parsing. */
export function isoDay(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (s.length < 10) return null;
  const day = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/** Days in a month, UTC. Day 0 of the NEXT month is the last day of this one. */
function daysInMonth(year, monthIndex) {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * The same day-of-month {@code months} earlier, clamped into that month.
 *
 * <p>Returns an ISO day, or null when the input is not a date. A negative or zero {@code months}
 * returns the input day unchanged rather than reaching forward: this function exists to compute a
 * cutoff BEFORE a bar, and a caller that asks for one after it has made a mistake that should not
 * become a chart.
 */
export function monthsBefore(lastBarDate, months) {
  const day = isoDay(lastBarDate);
  if (!day) return null;
  const n = Number(months);
  if (!Number.isFinite(n) || n <= 0) return day;

  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(5, 7)) - 1;
  const d = Number(day.slice(8, 10));

  // Month arithmetic on a plain integer, so the year rolls back without any Date involvement.
  const target = m - Math.floor(n);
  const ty = y + Math.floor(target / 12);
  // `%` is signed in JS, so a negative target needs the +12 wrap.
  const tm = ((target % 12) + 12) % 12;
  const td = Math.min(d, daysInMonth(ty, tm));

  const pad = (v, w) => String(v).padStart(w, '0');
  return `${pad(ty, 4)}-${pad(tm + 1, 2)}-${pad(td, 2)}`;
}

/**
 * THE THREE CUTOFFS for an account, or nulls for an account that has none.
 *
 * <h2>⚠ The LAST TRADED bar, never the padded axis</h2>
 *
 * `extendBarData` appends 250 synthetic weekdays past the last real bar, so the chart's final
 * column is about a YEAR ahead of the last price. Measured from there, a one-month CTS cutoff
 * lands eleven months into the future of real price action and withholds nothing whatever —
 * a filter that is on, correct in form, and inert. Callers pass `lastTradedDate` for that reason.
 *
 * <h2>⚠ NULL rather than a far-future sentinel</h2>
 *
 * "This account has no cutoff" and "this account's cutoff happens to be past everything" are
 * different states, and only one of them should survive a later change to how a cutoff is stored.
 * The same reasoning `gateIntervals` gives for its own null boundary.
 *
 * <h2>A missing PROFILE withholds nothing</h2>
 *
 * `isTrial` is false for the fallback user `AuthContext` keeps when `/api/user/me` never answered,
 * so a network blip shows the whole study rather than a truncated one. That is the posture the
 * rest of the app already takes, and for the stated reason: a missing profile is a transient fault
 * and a missing entitlement is an account fact, and the two must never look alike. Withholding
 * here would make a backend hiccup read as a downgrade.
 *
 * @param {string|number|Date|null} lastTradedDate the last bar the market actually traded
 * @param {{isTrial?: boolean, isAdmin?: boolean}} account
 * @returns {{cts: string|null, pta: string|null, ptb: string|null, isTrial: boolean}}
 */
export function trialCutoffDates(lastTradedDate, { isTrial = false, isAdmin = false } = {}) {
  const none = { cts: null, pta: null, ptb: null, isTrial: false };
  // ADMIN WINS. The two flags are mutually exclusive as `AuthContext` derives them from one
  // `subscriptionType`, but `isAdminUser` reads `role` and `admin` as well — so a document
  // spelling admin one way and TRIAL the other resolves to both. An administrator seeing a
  // truncated study would read as the chart being broken, which is the worse failure.
  if (isAdmin || !isTrial) return none;
  const day = isoDay(lastTradedDate);
  if (!day) return none;
  return {
    cts: monthsBefore(day, TRIAL_CUTOFF_MONTHS.CTS),
    pta: monthsBefore(day, TRIAL_CUTOFF_MONTHS.PTA),
    ptb: monthsBefore(day, TRIAL_CUTOFF_MONTHS.PTB),
    isTrial: true,
  };
}

/** The cutoff for a cycle key — `'long'` is PTA and `'short'` is PTB, as everywhere else. */
export function cutoffForCycle(cutoffs, cycleKey) {
  if (!cutoffs) return null;
  return cycleKey === 'long' ? (cutoffs.pta || null) : (cutoffs.ptb || null);
}

/**
 * Whether a date may be drawn.
 *
 * <p>A null cutoff admits everything. A mark ON the cutoff day is KEPT — the brief withholds what
 * falls STRICTLY after it, so the boundary is inclusive. A date that cannot be parsed is also
 * kept: dropping a mark because its own date was unreadable would withhold for a reason that has
 * nothing to do with the account.
 */
export function withinCutoff(date, cutoff) {
  if (!cutoff) return true;
  const day = isoDay(date);
  if (!day) return true;
  return day <= cutoff;
}

/**
 * A point list split at the cutoff.
 *
 * <p>Returns the kept array and a count, because the count is what the status line needs: a trial
 * reader looking at a thin scan has no way to tell a withheld month from a quiet sky, and those
 * must not look alike. The same argument the CTS cooldown's own `folded` count already makes.
 *
 * <p>The ARRAY IDENTITY is preserved when nothing is withheld, so a non-trial run hands the
 * renderer the very array it built rather than a copy — which also means a caller cannot tell
 * "filter applied, nothing matched" from "no filter" by looking, and does not need to.
 */
export function filterToCutoff(points, cutoff) {
  const list = Array.isArray(points) ? points : [];
  if (!cutoff) return { kept: list, withheld: 0 };
  const kept = list.filter((p) => withinCutoff(p && p.date, cutoff));
  return { kept, withheld: list.length - kept.length };
}

/**
 * Study intervals bounded by the cutoff: a pair starting past it is DROPPED, a pair straddling it
 * is CLAMPED to it.
 *
 * <h2>Why both, rather than dropping every pair that reaches past</h2>
 *
 * An interval that began inside the allowed window is a real, allowed event — the cycle genuinely
 * started where the pillar says. Dropping it would withhold history the account is entitled to in
 * order to withhold the few days at its tail, and the brief allows either ("filter out OR
 * terminate"). Clamping withholds exactly the part that is past the cutoff and nothing else.
 *
 * <p>Both ends are normalised to ISO days first, so a pair supplied end-first or carrying a time
 * still clamps correctly. A pair that loses its ordering to the clamp (both ends past the cutoff,
 * which the drop above already catches) cannot reach the output.
 *
 * @param {Array<Array<string>|{startDate?:string,endDate?:string,date1?:string,date2?:string}>} pairs
 * @param {string|null} cutoff
 * @returns {{pairs: Array, dropped: number, clamped: number}} pairs as two-element ARRAYS, which
 *          is the shape `/planet-in-sign` answers with and `pairDates` reads
 */
export function clampPairsToCutoff(pairs, cutoff) {
  const list = Array.isArray(pairs) ? pairs : [];
  if (!cutoff) return { pairs: list, dropped: 0, clamped: 0 };

  const out = [];
  let dropped = 0;
  let clamped = 0;
  list.forEach((pair) => {
    const [rawStart, rawEnd] = Array.isArray(pair)
      ? [pair[0], pair[1]]
      : [pair && (pair.startDate || pair.date1), pair && (pair.endDate || pair.date2)];
    const start = isoDay(rawStart);
    const end = isoDay(rawEnd);
    // Unreadable ends are passed through untouched rather than discarded, for the reason
    // `withinCutoff` gives: a pair this function cannot parse is not a pair the account is
    // not entitled to, and `intervalIndices` already drops what it cannot resolve.
    if (!start || !end) { out.push(pair); return; }
    if (start > cutoff) { dropped++; return; }
    if (end > cutoff) { clamped++; out.push([start, cutoff]); return; }
    out.push(pair);
  });
  return { pairs: out, dropped, clamped };
}

/**
 * The narrowest of several right-hand bounds, or null when there is no bound at all.
 *
 * <p>A replay playhead and a trial cutoff are two unrelated reasons a column may not be drawn on,
 * and when both are in force the narrower one is the answer. Combining them here rather than at
 * the call site keeps "which bound won" from being a nested ternary somebody has to read twice.
 */
export function narrowestBound(...bounds) {
  const finite = bounds.filter((b) => Number.isFinite(b));
  return finite.length === 0 ? null : Math.min(...finite);
}

/**
 * THE SENTENCE a trial reader needs, or '' when nothing was withheld.
 *
 * <p>Appended to a study's own status line rather than shown as a warning: it is a fact about the
 * account, not a failure, and a dialog would interrupt a reading it is only annotating. It names
 * the DATE as well as the count, because "17 withheld" invites the reader to look for a fault
 * while "nothing after 2026-09-30" is an answer they can act on.
 */
export function cutoffNote(label, withheld, cutoff) {
  if (!cutoff || !withheld) return '';
  const n = Number(withheld) || 0;
  if (n <= 0) return '';
  return ` Trial view: ${n.toLocaleString()} ${label}`
    + `${n === 1 ? '' : 's'} after ${cutoff} withheld.`;
}

/**
 * ...and the sentence for the case where EVERYTHING is withheld, which is a different message.
 *
 * <p>On a chart whose history is shorter than the cutoff — a recent listing, or any symbol
 * viewed with the nine-month PTA window — the cutoff precedes the first bar and the study draws
 * nothing at all. An empty chart with a count of withheld marks reads as a failed scan; this says
 * which it is, and it is the brief's own short-window edge case.
 */
export function cutoffEmptyNote(label, cutoff, firstBarDate) {
  if (!cutoff) return '';
  const first = isoDay(firstBarDate);
  const because = first && first > cutoff
    ? ` — this chart starts at ${first}, after that date`
    : '';
  return ` Trial view: nothing is drawn after ${cutoff}, and ${label} has nothing before it`
    + `${because}.`;
}

export default trialCutoffDates;
